/**
 * Cart state — single in-memory source of truth for the cart object.
 *
 * Listens to `cart:update` BEFORE any custom element does (registered at
 * module load), so by the time `<cart-items>` / `<cart-icon-bubble>` /
 * `<cart-drawer>` see the event, `event.detail.cart.normalized` has been
 * populated and they can read the deducted item count without
 * reimplementing the Service-skip loop.
 *
 * Phase 13: cart state is owned by this module and broadcast via
 * `cart:update`. The legacy `app.set('customer.cart', cart)` bridge —
 * originally kept "for currencies.js" — was retired once an audit
 * confirmed nothing in the codebase actually reads `app.get('customer.cart')`.
 *
 * Source path: theme/js/core/cart-state.esm.js
 * Built name : ./cart-state.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Opus 4.7
 *
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { parseFragment } from './dom.js';

// Single source of truth for the hemming product handle. Two Liquid
// files (`snippets/cart-contents.liquid`, `sections/cart-icon-bubble.liquid`)
// hardcode the same literal — `tests/static/cart.test.js` pins all three
// to identical strings so a rename can't drift the JS-derived count from
// the Liquid-rendered count.
export const HEMMING_PRODUCT_HANDLE = 'chainstitch-hemming';

let _cart = null;

export function normalizeCartData(cart) {
  let itemCount = parseInt(cart.item_count, 10) || 0;
  let hasHemmingItem = false;
  (cart.items || []).forEach((item) => {
    if (item.handle === HEMMING_PRODUCT_HANDLE) {
      itemCount -= parseInt(item.quantity, 10) || 0;
      hasHemmingItem = true;
    }
  });
  return {
    item_count: itemCount,
    total_price: cart.total_price,
    has_hemming_item: hasHemmingItem,
  };
}

// Parse a typed cart-state object from the HTML string of the
// cart-icon-bubble section response. The section embeds a
// <script data-cart-state> block whose JSON is already server-normalized
// (hem items deducted, items array with variant_id + hem_length). Reading
// from the response string before DOM insertion is more rigorous than
// scraping attributes from live elements — the JSON schema is the contract.
export function parseCartStateFromSectionHtml(html) {
  if (!html) return null;
  try {
    const el = parseFragment(html, 'script[data-cart-state]');
    if (!el) return null;
    const data = JSON.parse(el.textContent);
    const items = data.items || [];
    return {
      item_count: data.item_count,
      total_price: data.total_price,
      items,
      normalized: {
        item_count: data.item_count,
        total_price: data.total_price,
        has_hemming_item: data.has_hemming_item || false,
      },
    };
  } catch (e) {
    log('[Cart] parseCartStateFromSectionHtml failed', e);
    return null;
  }
}

function setCart(cart) {
  if (!cart) return;
  // Shallow-clone before attaching `.normalized` so we never mutate the
  // input. `event.detail.cart` is shared across listeners, and writing
  // `.normalized` directly onto the caller's object surfaced state to
  // unrelated handlers retroactively.
  _cart = { ...cart };
  // Skip normalizeCartData when the cart was parsed from Liquid-rendered
  // section HTML — it already carries a pre-computed `normalized` object
  // with the hem-deducted item_count. Calling normalizeCartData again
  // would double-deduct hemming quantities.
  if (!_cart.normalized) {
    _cart.normalized = normalizeCartData(cart);
  }
  log('[Cart] built', {
    items: _cart.normalized.item_count,
    total: _cart.total_price,
  });
}

document.addEventListener('cart:update', (event) => {
  if (event && event.detail && event.detail.cart) {
    setCart(event.detail.cart);
  }
});

export function getCart(callback) {
  if (_cart) {
    if (typeof callback === 'function') callback(_cart);
    return Promise.resolve(_cart);
  }
  return fetch('/cart.js', { credentials: 'same-origin' })
    .then((r) => r.json())
    .then((cart) => {
      setCart(cart);
      if (typeof callback === 'function') callback(_cart);
      return _cart;
    });
}

export function getCurrentCart() {
  return _cart;
}

// Hydrate directly from `ep_appinfo.customer.cart` — the inline Liquid
// script sets this before any deferred module evaluates, while
// `app.properties` is populated later on DOMContentLoaded. Reading via
// `appGet` here returns undefined and leaves `_cart` null, which broke
// the cart-button click handler ("cart is empty" flash on a non-empty
// cart from a previous session).
const initial =
  (window.ep_appinfo && window.ep_appinfo.customer && window.ep_appinfo.customer.cart) ||
  appGet('customer.cart');
if (initial) setCart(initial);

log('[Cart] state loaded');
